<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { categoryVisualSrc } from "~/utils/categories";
import { SECONDARY_NAV_ITEMS } from "~/constants/secondaryNavigation";
import { isDrivePath } from '~/utils/drive-route'
import { isOrderPath } from '~/utils/order-route'
import { shopRootSlug } from "~/utils/categoryTree";
import { parseProductSort } from '~/utils/productSort'

const { categories, fetchCategories } = useCategories();
const { requestCategoryReset } = useCategoryFilterReset();

const switchLocalePath = useSwitchLocalePath();

const cartStore = useCartStore();
const { totalCount } = storeToRefs(cartStore);
const { count: favouritesCount } = storeToRefs(useFavouritesStore());

// Triggered by the add action, not by totalCount: a persisted cart restored on load must not pulse.
const cartPulse = ref(false);
let cartPulseTimer: ReturnType<typeof setTimeout> | undefined;

cartStore.$onAction(({ name, after }) => {
  if (name !== 'addProduct' && name !== 'addCartItems') return;
  const wasEmpty = cartStore.items.length === 0;
  after(() => {
    if (!wasEmpty || cartStore.items.length === 0) return;
    cartPulse.value = true;
    clearTimeout(cartPulseTimer);
    cartPulseTimer = setTimeout(() => { cartPulse.value = false; }, 500);
  });
});

const localePath = useLocalePath();

const {t, locale} = useI18n();

const searchQuery = ref('');
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

watch(locale, () => {
  fetchCategories();
});

const cart = ref();
const cartOpen = ref(false);
const menuOpen = ref(false);
const searchFocused = ref(false);
const toggleCart = (event: any) => {
   cart.value.toggle(event)
}

let scrollLockActive = false;

const isOverlayScrollTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return !!target.closest('.side-nav, .p-popover, [data-pc-name="popover"]');
};

const preventBackgroundScroll = (event: TouchEvent | WheelEvent) => {
  if (isOverlayScrollTarget(event.target)) return;
  event.preventDefault();
};

const clearPageScrollLock = () => {
  if (typeof document === 'undefined') return;
  if (scrollLockActive) {
    document.removeEventListener('touchmove', preventBackgroundScroll);
    document.removeEventListener('wheel', preventBackgroundScroll);
    scrollLockActive = false;
  }
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
};

const setPageScrollLocked = () => {
  if (typeof document === 'undefined') return;
  // Search is an inline header field, not an overlay — locking the page
  // prevents scrolling results while the box is open.
  const locked = cartOpen.value || menuOpen.value;

  // Avoid overflow:hidden on html/body — it breaks position:sticky and can hide
  // the header + absolute side menu while scrolled down.
  if (locked && !scrollLockActive) {
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    scrollLockActive = true;
  } else if (!locked && scrollLockActive) {
    document.removeEventListener('touchmove', preventBackgroundScroll);
    document.removeEventListener('wheel', preventBackgroundScroll);
    scrollLockActive = false;
  }
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';

  if (locked && window.scrollX !== 0) {
    window.scrollTo({ top: window.scrollY, left: 0 });
  }
};

watch([cartOpen, menuOpen], setPageScrollLocked);

const onSearchFocus = () => {
  searchFocused.value = true;
  if (window.scrollX !== 0) {
    window.scrollTo({ top: window.scrollY, left: 0 });
  }
};

const onSearchBlur = () => {
  searchFocused.value = false;
};

function navigateToSearch(q: string) {
  const trimmed = q.trim();
  if (trimmed.length < 3) return;
  const searchPath = localePath('/search');
  const onSearch = route.path === searchPath || route.path === `${searchPath}/`;
  const sort = onSearch ? parseProductSort(route.query.sort) : null;
  const path = localePath(`/search?q=${trimmed}${sort ? `&sort=${sort}` : ''}`);
  if (route.fullPath !== path) {
    navigateTo(path);
  }
}

function onSearchEnter() {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
    searchTimeout = null;
  }
  navigateToSearch(searchQuery.value);
}

watch(searchQuery, (newValue) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (newValue.trim().length >= 3) {
    searchTimeout = setTimeout(() => {
      navigateToSearch(newValue);
    }, 800);
  }
});

const { locales } = useI18n();
const localeOptions = computed(() =>
  (locales.value as { code: string }[]).map(l => ({
    code: l.code,
    image: `/images/locales/${l.code}.avif`,
  }))
);
const selectedLocale = computed(() => locale.value);

const otherLocale = computed(() =>
  localeOptions.value.find(option => option.code !== selectedLocale.value)?.code ?? 'en',
);

const toggleLocale = () => {
  if (otherLocale.value === selectedLocale.value) return
  navigateTo(switchLocalePath(otherLocale.value as 'en' | 'bg'))
};

const accountComingSoonVisible = ref(false);
function closeAccountComingSoon() {
  accountComingSoonVisible.value = false;
}

const { isLogged, user } = storeToRefs(useAuthStore());
const authModalVisible = useState('auth-modal-visible', () => false);

const accountInitial = computed(() => {
  const fromName = (user.value?.firstName || user.value?.lastName || '').trim().charAt(0)
  const fromEmail = (user.value?.email || '').trim().charAt(0)
  const letter = fromName || fromEmail
  return letter ? letter.toLocaleUpperCase(locale.value) : ''
})

function openAccount() {
  if (isLogged.value) {
    navigateTo(localePath('/account'));
  } else {
    accountComingSoonVisible.value = true;
  }
}

function toggleSideMenu() {
  menuOpen.value = !menuOpen.value
}

function closeSideMenu() {
  menuOpen.value = false
}

const mobileNavRef = ref<HTMLElement | null>(null);

const scrollActiveNavItemToCenter = () => {
  if (!import.meta.client) return;
  nextTick(() => {
    if (!mobileNavRef.value) return;
    const active = mobileNavRef.value.querySelector('a.router-link-active') as HTMLElement | null;
    if (!active) return;
    const nav = mobileNavRef.value;
    const navWidth = nav.offsetWidth;
    if (!navWidth) return;
    const itemLeft = active.closest('li')?.offsetLeft ?? active.offsetLeft;
    const itemWidth = (active.closest('li') as HTMLElement)?.offsetWidth ?? active.offsetWidth;
    nav.scrollTo({ left: itemLeft - navWidth / 2 + itemWidth / 2, behavior: 'smooth' });
  });
};

const route = useRoute();
const router = useRouter();
const isDriveRoute = computed(() => isDrivePath(route.path));
const isOrderRoute = computed(() => isOrderPath(route.path));

function goOrderBack() {
  if (router.options.history.state.back) {
    router.back();
    return;
  }
  navigateTo(localePath('/your-cart'));
}

const { bySlug } = useCategoryTree();
const activeNavRoot = computed(() => {
  const param = route.params.categorySlug;
  const slug = typeof param === 'string'
    ? param
    : Array.isArray(param) ? param[0] : undefined;
  if (!slug) return null;
  return shopRootSlug(slug, bySlug.value);
});
function isNavRootActive(slug: string) {
  return activeNavRoot.value === slug;
}
watch(() => route.fullPath, () => {
  cart.value?.hide();
  closeSideMenu();
});
watch([() => route.path, activeNavRoot], scrollActiveNavItemToCenter);

const stopMobileNavMomentum = () => {
  const nav = mobileNavRef.value;
  if (!nav) return;
  nav.scrollLeft = nav.scrollLeft;
};

onMounted(() => {
  mobileNavRef.value?.addEventListener('touchstart', stopMobileNavMomentum, { passive: true });
  scrollActiveNavItemToCenter();
});
onUnmounted(() => {
  mobileNavRef.value?.removeEventListener('touchstart', stopMobileNavMomentum);
  clearPageScrollLock();
  clearTimeout(cartPulseTimer);
});

</script>

<template>
  <header :class="{ 'drive-compact': isDriveRoute, 'order-compact': isOrderRoute }">
    <section class="bg-dark-blue">
      <div class="content" id="utility-bar">
        <NuxtLink v-if="!isOrderRoute" :to="localePath('/')" class="desktop-logo">
          <NuxtImg id="logo" src="/images/logo/logo-vodi4ka-header.png" alt="Vodi4ka logo" width="124" height="111"/>
        </NuxtLink>
        <span v-if="isDriveRoute" class="drive-header-title">{{ t('utility.driveRoute') }}</span>
        <button
          v-if="isOrderRoute"
          type="button"
          class="order-back-btn"
          :aria-label="t('order.backAria')"
          @click="goOrderBack"
        >
          <Icon name="tabler:arrow-left" size="1.35rem" />
        </button>
        <div v-if="isOrderRoute" class="order-header-cluster">
          <span class="order-header-title">{{ t('order.checkoutHeaderTitle') }}</span>
        </div>
        <div id="contacts">
          <ul :data-label="isDriveRoute ? t('utility.officeContact') : t('utility.quickOrders')">
            <li>
              <a
                href="tel:+35929463202"
                class="phone-contact"
                :aria-label="`${t('utility.callAria')}: ${t('utility.phoneNumber')}`"
                :data-tooltip="t('utility.phoneNumber')"
              >
                <img src="/icons/phone.svg" alt="phone icon" width="20" height="20"/>
                {{ t('utility.phone') }}
              </a>
            </li>
            <li>
              <a href="viber://chat?number=%2B359888637177" :aria-label="t('utility.viberAria')">
                <img src="/icons/viber.svg" alt="viber icon" width="20" height="20"/>
                Viber
              </a>
            </li>
            <li class="utility-quick">
              <NuxtLink :to="localePath('/express-order')" :aria-label="t('expressOrder.title')">
                <img src="/icons/white-cart-fast-order.avif" alt="fast order cart icon" width="22" height="20"/>
                {{ t('utility.quick') }}
              </NuxtLink>
            </li>
          </ul>
        </div>
        <input id="search" v-model="searchQuery" :placeholder="t('utility.searchPlaceholder')" @keydown.enter="onSearchEnter"/>
        <div class="locale-switcher">
          <button
            type="button"
            class="locale-current"
            :aria-label="t('utility.switchLocale', { locale: otherLocale.toUpperCase() })"
            @click="toggleLocale"
          >
            <img :src="`/images/locales/${selectedLocale}.avif`" :alt="selectedLocale" width="24" height="24" />
          </button>
        </div>
        <ul id="utilities">
          <li>
            <button
              type="button"
              :aria-label="isLogged ? t('auth.openProfile') : t('auth.open')"
              class="account-icon-wrapper utility-button"
              @click="openAccount"
            >
              <span
                v-if="isLogged && accountInitial"
                class="account-initial"
                aria-hidden="true"
              >{{ accountInitial }}</span>
              <img
                v-else
                src="/icons/account.svg"
                alt=""
                width="24"
                height="24"
              >
            </button>
          </li>
          <li v-if="!isOrderRoute">
            <button
              type="button"
              class="fav-icon-wrapper utility-button"
              :aria-label="t('utility.favouritesAria')"
              @click="navigateTo(localePath('/favourites'))"
            >
              <img src="/icons/favourite.svg" alt="Favourite icon" width="24" height="24">
              <span v-if="favouritesCount > 0" class="fav-badge">{{ favouritesCount }}</span>
            </button>
          </li>
          <li v-if="!isOrderRoute">
            <button
              type="button"
              class="cart-icon-wrapper utility-button"
              :class="{ 'cart-pulse': cartPulse }"
              :aria-label="t('utility.cartAria')"
              @click="toggleCart"
            >
              <img src="/icons/cart.svg" alt="Cart icon" width="24" height="24">
              <span v-if="totalCount > 0" class="cart-badge">{{ totalCount }}</span>
            </button>
          </li>
        </ul>
        <Popover unstyled ref="cart" @show="cartOpen = true" @hide="cartOpen = false">
          <Cart @close="cart.hide()"></Cart>
        </Popover>
        <Teleport to="body">
          <div v-if="cartOpen" class="cart-backdrop" @click="cart.hide()"></div>
        </Teleport>
        <AppConfirmDialog
          v-model:visible="accountComingSoonVisible"
          :close-aria-label="t('auth.comingSoon.close')"
          :confirm-label="t('auth.comingSoon.confirm')"
          @confirm="closeAccountComingSoon"
        >
          <template #title>{{ t('auth.comingSoon.title') }}</template>
          <template #default>
            <p class="app-dialog-body">{{ t('auth.comingSoon.body') }}</p>
            <p class="app-dialog-body">{{ t('auth.comingSoon.followUp') }}</p>
          </template>
        </AppConfirmDialog>
        <LoginRegisterModal v-model="authModalVisible" />
      </div>
    </section>
    <div id="side-menu-wrapper">
      <button
        type="button"
        class="hamburger"
        :class="{ open: menuOpen }"
        :aria-expanded="menuOpen"
        aria-controls="side-menu"
        :aria-label="t('utility.navMenuAria')"
        @click="toggleSideMenu"
      ></button>
      <div class="mobile-search" :class="{ focused: searchFocused }">
        <input
          type="text"
          class="search-box"
          v-model="searchQuery"
          :placeholder="t('utility.searchPlaceholder')"
          @keydown.enter="onSearchEnter"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
        >
      </div>
      <NuxtLink :to="localePath('/')" class="mobile-logo">
        <NuxtImg src="/images/logo/logo-nav.avif" alt="Vodi4ka logo" width="124" height="111"/>
      </NuxtLink>
      <aside id="side-menu" class="side-nav" :class="{ open: menuOpen }">
        <h4 class="nunito black font-20 dark-blue">{{ t('utility.shopHeading') }}</h4>
        <nav>
          <ul>
            <li v-for="category in categories" :key="category.id" class="uppercase nunito bold font-14">
              <NuxtLink
                :to="localePath(`/category/${category.slug}`)"
                :class="{ 'router-link-active': isNavRootActive(category.slug) }"
                :aria-current="isNavRootActive(category.slug) ? 'page' : undefined"
                @click="requestCategoryReset(category.slug); closeSideMenu()"
              >
                <img :src="categoryVisualSrc(category)" alt="" width="27" height="27">{{ category.itemName }}
              </NuxtLink>
            </li>
          </ul>
        </nav>
        <h4 class="montserrat extra-bold font-20 accent">{{ t('utility.servicesHeading') }}</h4>
        <ul>
          <li v-for="item in SECONDARY_NAV_ITEMS" :key="item.key" class="montserrat blue">
            <NuxtLink :to="localePath(item.path)" @click="closeSideMenu">
              <img :src="item.icon" alt="" width="27" height="27">{{ t(item.key) }}
            </NuxtLink>
          </li>
        </ul>
        <h4 class="montserrat extra-bold font-20">{{ t('utility.contactsHeading') }}</h4>
        <ul>
          <li class="montserrat blue"><img src="/icons/phone-blue.svg"
                                           alt="" width="37" height="37">
            {{ t('utility.ordersService') }}
          </li>
          <ul>
            <li class="pl-3"><a href="tel:+35929463202">02 946 32 02</a></li>
            <li class="pl-3"><a href="tel:+35924837205">02 483 72 05</a></li>
            <li class="pl-3"><a href="tel:+359888483163">0888 483 163</a></li>
          </ul>
          <li class="montserrat blue"><img src="/icons/phone-blue.svg"
                                           alt="" width="37" height="37">
            {{ t('utility.salesDept') }}
          </li>
          <ul>
            <li class="pl-3"><a href="tel:+359889514026">0889 514 026</a></li>
          </ul>
          <li class="montserrat blue"><img src="/icons/phone-blue.svg"
                                           alt="" width="37" height="37">
            {{ t('utility.serviceDept') }}
          </li>
          <ul>
            <li class="pl-3"><a href="tel:+359884123570">0884 123 570</a></li>
          </ul>
          <li class="montserrat blue"><img src="/icons/viber-blue.svg"
                                           alt="" width="37" height="37">
            Viber:
          </li>
          <ul>
            <li class="pl-3"><a href="viber://chat?number=%2B359888637177">0888637177</a></li>
          </ul>
          <li class="montserrat blue"><img src="/icons/messanger-blue.svg"
                                           alt="" width="37" height="37">
            Messenger:
          </li>
          <ul>
            <li class="pl-3"><a href="https://m.me/vodi4ka.com1">{{ t('utility.messengerWrite') }}</a></li>
          </ul>
          <li class="montserrat blue"><img src="/icons/mail-blue.svg"
                                           alt="" width="37" height="37">
            Email:
          </li>
          <ul>
            <li class="pl-3"><a href="mailto:info@vodi4ka.com">info@vodi4ka.com</a></li>
          </ul>
        </ul>
        <div class="side-locale">
          <button
            type="button"
            class="locale-current"
            :aria-label="t('utility.switchLocale', { locale: otherLocale.toUpperCase() })"
            @click="toggleLocale"
          >
            <img :src="`/images/locales/${selectedLocale}.avif`" :alt="selectedLocale" width="24" height="24" />
          </button>
          <span class="side-locale-current">{{ t(selectedLocale === 'en' ? 'utility.languageEn' : 'utility.languageBg') }}</span>
        </div>
      </aside>
    </div>
    <!-- Desktop navigation -->
    <div id="navigation">
      <nav id="primary">
        <ul class="content">
          <li
            v-for="(category, index) in categories"
            :key="category.id"
            :class="{ 'second-row': index >= Math.ceil(categories.length / 2) }"
          >
            <NuxtLink
              :to="localePath(`/category/${category.slug}`)"
              :class="{ 'router-link-active': isNavRootActive(category.slug) }"
              :aria-current="isNavRootActive(category.slug) ? 'page' : undefined"
              @click="requestCategoryReset(category.slug)"
            >
              <img :src="categoryVisualSrc(category)" alt="" width="27" height="27">{{ category.itemName }}
            </NuxtLink>
          </li>
        </ul>
      </nav>
      <nav id="secondary">
        <ul class="content">
          <li v-for="item in SECONDARY_NAV_ITEMS" :key="item.key" class="montserrat blue">
            <NuxtLink :to="localePath(item.path)">
              <img :src="item.icon" alt="" width="27" height="27">{{ t(item.key) }}
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Mobile combined navigation -->
    <nav ref="mobileNavRef" class="mobile-nav">
      <ul>
        <li v-for="category in categories" :key="category.id" class="mobile-nav-item">
          <NuxtLink
            :to="localePath(`/category/${category.slug}`)"
            :class="{ 'router-link-active': isNavRootActive(category.slug) }"
            :aria-current="isNavRootActive(category.slug) ? 'page' : undefined"
            @click="requestCategoryReset(category.slug)"
          >
            <img :src="categoryVisualSrc(category)" alt="" width="27" height="27">{{ category.itemName }}
          </NuxtLink>
        </li>
        <li v-for="item in SECONDARY_NAV_ITEMS" :key="item.key" class="mobile-nav-item secondary">
          <NuxtLink :to="localePath(item.path)">
            <img :src="item.icon" alt="" width="27" height="27">{{ t(item.key) }}
          </NuxtLink>
        </li>
      </ul>
    </nav>
  </header>
</template>

<style scoped>

header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: var(--white);

  @media (max-width: 768px) {
    overflow-x: clip;
  }
}

/*
 * Reserve the logo's box before the PNG loads. `width: auto` + `aspect-ratio: auto`
 * dropped the width/height attributes' ratio, so the logo was 0px wide until it
 * loaded and the whole utility bar then shifted ~53px. Content box: 70px minus
 * the padding, width from the file's 124×111 ratio (same size as before).
 */
#logo {
  box-sizing: content-box;
  width: auto;
  height: calc(70px - 1.4rem);
  aspect-ratio: 124 / 111;
  padding-top: .7rem;
  padding-bottom: .7rem;
}

.desktop-logo {
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
}

#navigation {
  @media (max-width: 768px) {
    display: none;
  }
}


#utility-bar {
  padding-left: 1rem;
  padding-right: 1rem;
  height: 75px;
  display: flex;
  width: 100%;
  gap: 20px;
  /*justify-content: space-between;*/
  align-items: center;

  #side-menu-wrapper {
    display: none;
  }

  #contacts {
    margin-left: 1rem;
    color: var(--white);
    flex-shrink: 0;

    ul {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 15px;
      font-size: 12px;
      border: 1px solid white;
      padding: 10px 1rem .5rem;
      border-radius: 40px;

      a {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      a.phone-contact {
        position: relative;
      }

      @media (hover: hover) and (pointer: fine) {
        a.phone-contact::after {
          content: attr(data-tooltip);
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background-color: var(--white);
          color: var(--dark-blue);
          border: 1px solid var(--dark-blue);
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          z-index: 110;
          transition: opacity 0.15s ease;
        }

        a.phone-contact:hover::after,
        a.phone-contact:focus-visible::after {
          opacity: 1;
        }
      }

      img {
        max-height: 20px;
        align-items: center;
        justify-items: center;
      }
    }

    ul::before {
      content: attr(data-label);
      text-transform: uppercase;
      font-size: 10px;
      font-weight: 700;
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      background-color: var(--dark-blue);
      padding: 0 6px;
      white-space: nowrap;
      width: max-content;
    }
  }

  #search {
    font-weight: 500;
    border: none;
    border-radius: 30px;
    padding-top: .5rem;
    padding-bottom: .5rem;
    background-color: var(--white) !important;
    color: var(--dark-blue) !important;
    box-shadow: none !important;
    -webkit-appearance: none;
    appearance: none;
    padding-left: 1rem;
    background-image: url("/icons/search.svg");
    background-size: 16px;
    background-repeat: no-repeat;
    background-position: right 1rem center;
    flex-grow: 1;
    max-width: 40%;
    justify-self: start;

    &::placeholder {
      color: rgba(53, 104, 181, 0.5);
    }

    &:focus {
      background-color: var(--white) !important;
      color: var(--dark-blue) !important;
      box-shadow: none !important;
      outline: none !important;
    }
  }

  #utilities {
    display: flex;
    align-items: center;
    gap: .5rem;
    flex-grow: 0;
    flex-shrink: 0;
    padding: 0;
    margin: 0;
    margin-left: auto;

    li {
      display: flex;
      align-items: center;
      list-style: none;
    }

    .utility-button {
      flex-shrink: 0;
    }

    .utility-button > img {
      width: 27px;
      height: 27px;
      flex-shrink: 0;
    }
  }

  @media (width <= 769px) {
    height: 65px;
    gap: 8px;

    > img {
      position: absolute;
      max-height: 70px;
      top: 70px;
      left: 50%;
      transform: translateX(-50%);

    }

    #contacts {
      margin-left: 0;
      flex: 1 1 auto;
      min-width: 0;
      container-type: inline-size;

      h5 {
        display: none;
      }

      ul {
        box-sizing: border-box;
        width: max-content;
        max-width: 100%;
        min-width: 0;
        gap: clamp(4px, 6cqi, 10px);
        padding: 10px clamp(4px, 6cqi, 10px) 8px;
        font-size: clamp(9px, 7.5cqi, 12px);

        li {
          min-width: 0;
        }

        a {
          line-height: 1;
          min-width: 0;
          white-space: nowrap;
        }

        img {
          max-height: clamp(14px, 12cqi, 20px);
          width: auto;
        }
      }

      ul::before {
        max-width: calc(100% - 8px);
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    #utilities {
      flex-shrink: 0;
    }

    #search {
      display: none;
    }
  }
}

#side-menu-wrapper {
  display: none;

  @media (max-width: 768px) {
    min-height: 60px;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-left: 1rem;
    padding-right: 1rem;
    position: relative;
    overflow-x: clip;
    border-bottom: 1px solid var(--ligth-blue);
  }
}

.mobile-search {
  position: relative;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  transition: padding 0.2s ease;
}

.mobile-search:focus-within,
.mobile-search.focused {
  padding-left: 0;
  padding-right: 0;
}

.mobile-logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    width: auto;
    max-height: 44px;
    aspect-ratio: auto;
  }
}


.hamburger {
  position: relative;
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-block;
  z-index: 4;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.3s ease;
}

@media (min-width: 768px) {
  .hamburger {
    display: none;
  }
}

.hamburger::before {
  content: "";
  display: inline-block;
  position: absolute;
  inset: 0;
  background-image: url("/icons/hamburger.svg");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 30px 30px;
}

.hamburger::after {
  content: "";
  display: inline-block;
  position: absolute;
  inset: 0;
  background-image: url("/icons/cross.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 20px 20px;
  opacity: 0;
}

.hamburger.open {
  z-index: 10;
}

.hamburger.open::before {
  opacity: 0;
}

.hamburger.open::after {
  opacity: 1;
}

.mobile-nav {
  display: none;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  border-bottom: 1px solid var(--ligth-blue);

  &::-webkit-scrollbar {
    display: none;
  }

  ul {
    display: flex;
    flex-shrink: 0;
    gap: 16px;
    padding: .5rem 1rem;
  }

  .mobile-nav-item {
    flex-shrink: 0;
    white-space: nowrap;
    width: fit-content;
    height: 48px;
    border: 1px solid var(--ligth-blue);
    padding: 0;
    border-radius: 7px;
    font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 12px;
    display: flex;
    align-items: center;
    touch-action: manipulation;

    a {
      cursor: pointer;
      display: flex;
      align-items: center;
      line-height: 120%;
      white-space: nowrap;
      text-align: left;
      height: 100%;
      width: 100%;
      padding: 0 .5rem;
      box-sizing: border-box;
      touch-action: manipulation;

      img {
        margin-right: .5rem;
        display: block;
        height: 27px;
        background-color: var(--white);
        border-radius: 50%;
        padding: 0.25rem;
        object-fit: contain;
        flex-shrink: 0;
      }
    }

    a:hover,
    a.router-link-active {
      border-radius: 10px;
      background-color: var(--white);
    }

    &:has(a.router-link-active) {
      background-color: var(--ligth-blue);

      a {
        color: var(--dark-blue);
        background-color: transparent;
      }

      img {
        background-color: var(--white);
      }
    }

    &.secondary {
      font-size: 10px;
      font-weight: 600;
      text-transform: none;

      a img {
        height: 20px;
        width: auto;
      }
    }
  }
}


#primary {
  width: 100%;
  background-color: var(--ligth-blue);
  font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  font-size: 12px;

  /*
   * The row count must not depend on the font: Montserrat arrives late and its
   * fallback is 1-2% wider, which used to wrap the last item (page jumps ~60px).
   * - Row at its full 1157px (.content max): always one line; items may shrink a
   *   few px below their text, so a wider fallback squeezes instead of wrapping.
   * - Narrower: always two balanced lines (forced break after the first half).
   */
  ul {
    display: flex;
    justify-content: space-between;
    gap: 0.1rem;
    flex-wrap: wrap;
    align-items: center;

    li {
      flex-shrink: 0;
    }

    /* 1157px + room for a classic scrollbar */
    @media (width >= 1180px) {
      flex-wrap: nowrap;

      li {
        flex-shrink: 1;
        min-width: 0;
      }

      /* A shrunk li lets its link overflow to the right; keep the last one inside. */
      li:last-child {
        flex-shrink: 0;
      }
    }

    @media (width < 1180px) {
      row-gap: 0;

      /* Line break between the halves; a half that doesn't fit still wraps. */
      &::after {
        content: "";
        flex-basis: 100%;
      }

      li.second-row {
        order: 1;
      }
    }

    li a {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      line-height: 120%;
      padding: .5rem .3rem;
      margin-top: .5rem;
      margin-bottom: .5rem;
      white-space: nowrap; /* prevent text from wrapping */
      text-align: left;

      img {
        margin-right: .5rem;
        margin-bottom: 0;
        display: block;
        height: 27px;
        background-color: var(--white);
        border-radius: 50%;
        padding: 0.25rem;
        object-fit: contain;
        flex-shrink: 0;
      }
    }

    a:hover,
    a.router-link-active {
      border-radius: 10px;
      background-color: var(--white);
    }
  }
}


#secondary {
  background-color: white;
  border-bottom: 1px solid var(--ligth-blue);

  font-size: 12px;
  font-weight: 600;
  justify-items: center;

  img {
    margin-right: .25rem;
    display: block;
    height: 12px;
    width: auto;
    object-fit: contain;
  }

  ul {
    padding-top: .5rem;
    padding-bottom: .5rem;
    display: flex;
    /*gap: 1rem;*/

    li a {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      line-height: 100%;
      padding: .5rem .75rem;
      border-radius: 15px;

    }

    li a:hover,
    li a.router-link-active {
      background-color: #EAF6FF;
    }
  }
}

.side-nav {
  position: absolute;
  border-radius: 2rem;
  padding: 2.75rem 3rem 2rem 5rem;
  background-color: var(--lightest-blue);
  top: 0;
  left: -32px;
  width: min(430px, calc(100% + 2rem));
  min-height: 200px;
  max-height: calc(100dvh - 75px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  z-index: 3;
  display: flex;
  flex-direction: column;
  color: var(--dark-blue);
  ul {
    margin-top: .5rem;
    display: flex;
    flex-direction: column;
  }

  li {
    margin-top: .5rem;
    display: inline-flex;
    align-items: center;
    font-family: 'Montserrat', sans-serif;

    a {
      display: inline-flex;
      align-items: center;
      gap: .5rem;
      width: 100%;
    }
  }

  img {
    background-color: var(--white);
    border-radius: 50%;
    display: block;
    height: 37px;
    width: auto;
    padding: 0.3rem;
    object-fit: contain;
    flex-shrink: 0;
  }

  h4 {
    text-align: left;
    display: inline-block;
    border-bottom: 1px solid;
    padding-bottom: 12px;

  }

  h4:nth-of-type(2), h4:nth-of-type(3) {
    margin-top: 4rem;
  }

}

.side-nav.open {
  transform: translateX(0);
}

.side-locale {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--ligth-blue);
}

.side-locale .locale-current {
  padding-left: 0;
  padding-right: 0;
}

.side-locale .locale-current img {
  background: none;
  padding: 0;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  object-fit: cover;
}

.side-locale-current {
  color: var(--dark-blue);
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
}


.search-box {
  font-size: var(--app-field-font-size);
  font-family: var(--font-app-field);
  font-size-adjust: var(--app-field-font-adjust);
  font-weight: 500;
  box-sizing: border-box;
  height: 28px;
  border: 1px solid var(--light-gray);
  border-radius: 30px;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 1rem;
  padding-right: 2.5rem;
  width: 100%;
  background-color: var(--white) !important;
  background-image: url("/icons/search.svg");
  background-size: 16px;
  background-repeat: no-repeat;
  background-position: right 1rem center;
  color: var(--dark-blue) !important;
  box-shadow: none !important;
  -webkit-appearance: none;
  appearance: none;
  transition: height 0.2s ease, padding 0.2s ease, border-color 0.15s ease;

  &::placeholder {
    color: rgba(53, 104, 181, 0.5);
  }

  &:focus {
    background-color: var(--white) !important;
    color: var(--dark-blue) !important;
    box-shadow: none !important;
    outline: none !important;
  }
}

.mobile-search:focus-within .search-box,
.mobile-search.focused .search-box {
  height: 40px;
  padding-top: .5rem;
  padding-bottom: .5rem;
  border-color: var(--dark-blue);
}

.cart-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 51, 111, 0.5);
  z-index: 999;
}

.cart-icon-wrapper,
.fav-icon-wrapper {
  position: relative;
  cursor: pointer;
}

.account-icon-wrapper {
  cursor: pointer;
}

.account-initial {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #7c6cf0;
  color: var(--white);
  font-family: 'Google Sans', Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 1px;
  text-transform: uppercase;
  user-select: none;
  flex-shrink: 0;
  font-synthesis: none;
}

.utility-button {
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-badge,
.fav-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background-color: var(--accent-red);
  color: var(--white);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  pointer-events: none;
}

/* First item in an empty cart: the badge pops in. */
.cart-pulse .cart-badge {
  animation: cart-badge-pop 400ms ease-out both;
}

@keyframes cart-badge-pop {
  0% { transform: scale(0); }
  60% { transform: scale(2); }
  100% { transform: scale(1); }
}

@keyframes cart-badge-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .cart-pulse .cart-badge {
    animation: cart-badge-fade 200ms ease-out both;
  }
}

/* ── Locale Switcher ── */
.locale-switcher {
  position: relative;
  margin-left: 0;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  #utility-bar .locale-switcher {
    display: none;
  }
}

.locale-current {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 6px;
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.locale-current img {
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 50%;
}

header.drive-compact #search,
header.drive-compact .locale-switcher,
header.drive-compact #utilities,
header.drive-compact #side-menu-wrapper,
header.drive-compact .mobile-nav,
header.drive-compact #navigation,
header.drive-compact .utility-quick {
  display: none !important;
}

header.drive-compact {
  border-bottom: 2px solid var(--white);
}

header.drive-compact .desktop-logo {
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 62px !important;
  height: 62px !important;
  flex-shrink: 0;
  background-color: var(--white);
  border-radius: 50%;
  overflow: hidden;
}

header.drive-compact #logo {
  max-height: 42px !important;
  padding: 0 !important;
}

header.drive-compact #utility-bar {
  justify-content: space-between;
  gap: 8px !important;
}

header.drive-compact .drive-header-title {
  color: var(--white);
  font-weight: 700;
  font-size: 1.15rem;
  line-height: 1.2;
  margin-left: 0 !important;
  flex: 1;
  min-width: 0;
}

header.drive-compact #contacts {
  display: flex;
  flex: 0 0 auto;
  min-width: 0;
  margin-left: auto;
  justify-content: flex-end;
}

header.drive-compact #utility-bar #contacts ul {
  display: flex;
  justify-content: center !important;
  width: fit-content !important;
  max-width: 100%;
  min-width: 0 !important;
  box-sizing: border-box;
  gap: 0.85rem !important;
  padding: 0.75rem 0.95rem 0.45rem !important;
}

header.drive-compact #utility-bar #contacts ul a {
  padding-left: 0.15rem;
  padding-right: 0.15rem;
}

header.drive-compact #utility-bar #contacts ul::before {
  content: attr(data-label);
  max-width: calc(100% - 8px);
  overflow: hidden;
  text-overflow: ellipsis;
}

header.order-compact {
  position: static;
}

header.order-compact #contacts,
header.order-compact #search,
header.order-compact .locale-switcher,
header.order-compact #side-menu-wrapper,
header.order-compact .mobile-nav,
header.order-compact #navigation {
  display: none !important;
}

header.order-compact #utility-bar {
  position: relative;
  justify-content: space-between;
  gap: 12px;
  max-width: none;
}

header.order-compact .order-header-cluster {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

header.order-compact .order-back-btn {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: var(--white);
  color: var(--dark-blue);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

header.order-compact .order-header-title {
  color: var(--white);
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1.25;
  margin: 0;
  white-space: nowrap;
  text-align: center;
}

header.order-compact #utilities {
  flex-grow: 0;
  margin-left: auto;
}

@media (max-width: 768px) {
  header.order-compact .order-header-title {
    font-size: 0.82rem;
    max-width: calc(100% - 200px);
    white-space: normal;
  }
}

</style>